import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getFirestore, doc, getDocs, collection } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const MuniDetail = () => {
  const { id } = useParams();
  const [muni, setMuni] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liveStreams, setLiveStreams] = useState([]);
  const [user, setUser] = useState(null);
  const [purchasedPrices, setPurchasedPrices] = useState([]);
  const [redirectToForbidden, setRedirectToForbidden] = useState(false);
  const navigate = useNavigate();
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);

  const db = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        onAuthStateChanged(auth, async (currentUser) => {
          if (currentUser) {
            setUser(currentUser);

            const paymentsRef = collection(
              db,
              "users",
              currentUser.uid,
              "payments"
            );
            const paymentsSnap = await getDocs(paymentsRef);
            const products = paymentsSnap.docs
              .filter((doc) => doc.data().status === "succeeded")
              .flatMap((doc) => {
                const data = doc.data();
                if (Array.isArray(data.items)) {
                  return data.items.map((item) => item.price.product);
                }
                return [];
              });
            setPurchasedPrices(products);

            const responseMuni = await axios.get(
              process.env.REACT_APP_MUNI_JSON_URL
            );
            const muniData = responseMuni.data.series.find(
              (item) => item.id === id
            );
            if (!muniData) {
              throw new Error("Muni not found");
            }

            if (muniData.prodId && !products.includes(muniData.prodId)) {
              setRedirectToForbidden(true);
              return;
            }

            setMuni(muniData);

            const responseStreams = await axios.get(
              process.env.REACT_APP_LIVE_STREAMS_JSON_URL
            );
            const liveStreamsData = responseStreams.data;
            setLiveStreams(liveStreamsData);

            updateEpisodesWithLiveStatus(muniData, liveStreamsData);
            setLoading(false);
          } else {
            const responseMuni = await axios.get(
              process.env.REACT_APP_MUNI_JSON_URL
            );
            const muniData = responseMuni.data.series.find(
              (item) => item.id === id
            );
            if (!muniData) {
              throw new Error("Muni not found");
            }

            if (muniData.prodId) {
              setRedirectToForbidden(true);
              return;
            }

            setMuni(muniData);

            const responseStreams = await axios.get(
              process.env.REACT_APP_LIVE_STREAMS_JSON_URL
            );
            const liveStreamsData = responseStreams.data;
            setLiveStreams(liveStreamsData);

            updateEpisodesWithLiveStatus(muniData, liveStreamsData);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [id, db, auth]);

  const updateEpisodesWithLiveStatus = async (muniData, liveStreamsData) => {
    if (muniData && muniData.episodes) {
      const updatedEpisodes = await Promise.all(
        muniData.episodes.map(async (episode) => {
          const video = episode.content.videos[0];
          const isLive =
            video.videoType === "HLS" &&
            (await checkLiveStatus(video.url, liveStreamsData));

          return {
            ...episode,
            isLive,
          };
        })
      );

      setEpisodes(updatedEpisodes);

      const firstLiveEpisode = updatedEpisodes.find(
        (episode) => episode.isLive
      );

      if (firstLiveEpisode) {
        const video = firstLiveEpisode.content.videos[0];
        const episodeName = video.url.split("/").slice(-3, -1).join("/");
        setVideoUrl(
          `https://video.isilive.ca/play/${episodeName}?auto_hide=true`
        );
        setCurrentlyPlaying(firstLiveEpisode.title);
      }
    }
  };

  const checkLiveStatus = async (url, liveStreamsData) => {
    try {
      const episodeName = url.split("/").slice(-3, -1).join("/");
      return liveStreamsData.includes(episodeName);
    } catch (error) {
      console.error("Error checking live status:", error);
      return false;
    }
  };

  const handleEpisodeClick = (episode) => {
    const video = episode.content.videos[0];
    if (video.videoType === "HLS" && episode.isLive) {
      const episodeName = video.url.split("/").slice(-3, -1).join("/");
      setVideoUrl(
        `https://video.isilive.ca/play/${episodeName}?auto_hide=true`
      );
    } else if (video.videoType === "MP4") {
      const episodeName = video.url.split("/").slice(3).join("/");
      setVideoUrl(
        `https://video.isilive.ca/play/${episodeName}?auto_hide=true`
      );
    } else {
      return;
    }
    setCurrentlyPlaying(episode.title);
  };

  if (redirectToForbidden) {
    navigate("/");
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          Error: {error}
        </div>
      </div>
    );
  }

  if (!muni) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          No page found for ID: {id}
        </div>
      </div>
    );
  }

  // Check if muni has "ppv" in its tags and render iframe HTML if true
  if (muni && muni.tags && muni.tags.includes("ppv")) {
    return (
      <>
        <p className="text-white text-wrap pl-4 pt-28">
          Please check the{" "}
          <a className="text-blue-600" href="/faq">
            F.A.Q. page
          </a>{" "}
          if you are experiencing issues with the live stream
        </p>
        <div className="flex items-center justify-center h-screen p-4">
          <iframe
            src={muni.embedLink}
            className="w-full h-full"
            title={muni.title}
            allowFullScreen
            frameBorder="0"
          ></iframe>
        </div>
      </>
    );
  }

  // Render the previous content if "ppv" is not present
  return (
    <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="p-4 py-10"></div>
        {videoUrl ? (
          <div className="relative" style={{ paddingTop: "56.25%" }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full p-4"
              key={videoUrl}
              src={videoUrl}
              allowFullScreen
              autoPlay
              title="Live Stream"
            ></iframe>
          </div>
        ) : (
          <img
            className="object-cover w-full p-4 rounded-3xl"
            src={muni.thumbnail}
            alt={muni.title}
          />
        )}
        <h2 className="text-white font-bold md:text-xl p-4 py-4">
          {muni.title}
        </h2>
        <p className="pl-4">{muni.longDescription}</p>
      </div>
      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="lg:py-10"></div>
        <h2 className="text-white font-bold md:text-xl pl-4">Videos:</h2>
        <ul className="divide-y divide-gray-300 p-4">
          {episodes.map((episode, index) => (
            <li
              key={index}
              className={`py-2 ${
                currentlyPlaying === episode.title
                  ? "bg-gray-700" // Highlight color for the currently playing video
                  : "bg-black/40"
              } ${
                episode.content.videos[0].videoType === "HLS"
                  ? episode.isLive
                    ? "cursor-pointer"
                    : ""
                  : "cursor-pointer"
              }`}
            >
              <h3
                className={`text-lg font-bold ${
                  episode.content.videos[0].videoType === "HLS"
                    ? episode.isLive
                      ? "hover:underline"
                      : "text-gray-500"
                    : "hover:underline"
                }`}
                onClick={() => handleEpisodeClick(episode)}
              >
                {episode.title}
                {episode.content.videos[0].videoType === "HLS" &&
                  (episode.isLive ? (
                    <span style={{ color: "red", marginLeft: "0.5rem" }}>
                      LIVE
                    </span>
                  ) : (
                    <span style={{ color: "gray", marginLeft: "0.5rem" }}>
                      Not Live
                    </span>
                  ))}
                {episode.content.videos[0].videoType === "MP4" && (
                  <span style={{ color: "green", marginLeft: "0.5rem" }}>
                    On Demand
                  </span>
                )}
              </h3>
              <p className="text-sm pl-4">{episode.shortDescription}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MuniDetail;
